import React from "react";
import { GroupedOnboardingTasks, OnboardingTaskStatus } from "../types";
import { OnboardingTaskItem } from "./OnboardingTaskItem";

interface OnboardingTaskListProps {
  tasks: GroupedOnboardingTasks;
  loading: boolean;
  onToggleStatus: (
    taskId: string,
    currentStatus: OnboardingTaskStatus,
  ) => Promise<void>;
  onToggleSubtask: (taskId: string, subtaskId: string) => Promise<void>;
}

export const OnboardingTaskList: React.FC<OnboardingTaskListProps> = ({
  tasks,
  loading,
  onToggleStatus,
  onToggleSubtask,
}) => {
  const totalTasks = tasks.pending.length + tasks.completed.length;
  const completedTasks = tasks.completed.length;
  const overallProgress =
    totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center text-gray-500 dark:text-gray-400">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4" />
          <p>Loading tasks...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8">
      <div className="max-w-3xl mx-auto">
        <div className="mb-8">
          <div className="mb-8">
            <h1 className="text-2xl md:text-3xl text-gray-800 dark:text-gray-100 font-bold">
              Start Here
            </h1>
            <p className="mt-2 text-gray-500 dark:text-gray-400">
              Complete these tasks to get the most out of your GCCA membership
            </p>

            <div className="mt-4 p-4 bg-white dark:bg-gray-800 rounded-xl shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium text-gray-800 dark:text-gray-100">
                  Overall Progress
                </span>
                <span className="text-sm font-semibold text-blue-500">
                  {completedTasks}/{totalTasks} completed
                </span>
              </div>
              <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-blue-500 transition-all duration-500"
                  style={{ width: `${overallProgress}%` }}
                />
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-semibold text-gray-800 dark:text-gray-100">
                  To Do
                </h2>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {tasks.pending.length}{" "}
                  {tasks.pending.length === 1 ? "task" : "tasks"}
                </span>
              </div>
              <div className="space-y-3 min-h-[50px] bg-gray-50 dark:bg-gray-900 p-4 rounded-lg">
                {tasks.pending.map((task) => (
                  <OnboardingTaskItem
                    key={task.id}
                    task={task}
                    onToggleStatus={onToggleStatus}
                    onToggleSubtask={onToggleSubtask}
                    isCompleted={false}
                  />
                ))}
                {tasks.pending.length === 0 && (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    <svg
                      className="w-12 h-12 mx-auto mb-3 text-green-500"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <p className="font-medium text-green-600">
                      All tasks completed!
                    </p>
                    <p className="text-sm">
                      Great job on finishing your onboarding
                    </p>
                  </div>
                )}
              </div>
            </div>

            {tasks.completed.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-semibold text-gray-800 dark:text-gray-100">
                    Completed
                  </h2>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {tasks.completed.length}{" "}
                    {tasks.completed.length === 1 ? "task" : "tasks"}
                  </span>
                </div>
                <div className="space-y-3 min-h-[50px] bg-gray-50 dark:bg-gray-900 p-4 rounded-lg">
                  {tasks.completed.map((task) => (
                    <OnboardingTaskItem
                      key={task.id}
                      task={task}
                      onToggleStatus={onToggleStatus}
                      onToggleSubtask={onToggleSubtask}
                      isCompleted={true}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
